import React, { SyntheticEvent, useContext, useState } from "react";

import { Alert, Button, CircularProgress, Grid, Snackbar } from "@mui/material";
import { useParams } from "react-router-dom";

import { ReconcileSubscriptionResponse } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/admin/v1/subscriptions_pb";

import SubscriptionAction from "./subscription-action";
import { reconcileSubscriptionMethod } from "api/endpoints/subscription";
import { callOptionsHeaders } from "api/helpers";
import { DEFAULT_SNACKBAR_DURATION, EventTypeReason } from "constants/index";
import { SnackbarContext } from "context/snackbar-context";
import { UrlParamTypes } from "types/org-display";

interface ReconcileSubscriptionProps {
  refreshOrgData: () => void;
}

const ReconcileSubscription = ({
  refreshOrgData,
}: ReconcileSubscriptionProps) => {
  const { regionCode, roleId } = useParams<UrlParamTypes>();
  const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
  const { showError } = useContext(SnackbarContext);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleClose = (
    event?: Event | SyntheticEvent<unknown, Event>,
    reason?: string | undefined
  ) => {
    if (reason === EventTypeReason.Clickaway) return;
    setSuccessMessage(null);
  };

  const formatSuccess = (resp: ReconcileSubscriptionResponse): string => {
    const entries = Object.entries(resp.seatsPurchased);
    if (entries.length === 0) return "Reconcile complete: no seats needed";
    return "Reconciled: " + entries.map(([k, v]) => `${k}: ${v}`).join(", ");
  };

  const handleReconcile = () => {
    setShowLoadingSpinner(true);
    reconcileSubscriptionMethod(
      { organizationId: roleId },
      callOptionsHeaders(regionCode)
    )
      .then((resp) => {
        setSuccessMessage(formatSuccess(resp));
        refreshOrgData();
      })
      .catch(showError)
      .finally(() => setShowLoadingSpinner(false));
  };

  return (
    <React.Fragment>
      <SubscriptionAction title="Reconcile Subscription:">
        {showLoadingSpinner ? (
          <Grid container justifyContent="center" sx={{ mt: 1, mr: 2 }}>
            <CircularProgress
              color="primary"
              variant="indeterminate"
              thickness={4}
              size={30}
            />
          </Grid>
        ) : (
          <Button
            sx={{ mt: 1, mr: 2 }}
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleReconcile}
            data-testid="reconcile-subscription-btn"
          >
            Reconcile Subscription
          </Button>
        )}
        <Snackbar
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          open={Boolean(successMessage)}
          autoHideDuration={DEFAULT_SNACKBAR_DURATION}
          onClose={handleClose}
        >
          <Alert severity="success" variant="filled">
            {successMessage}
          </Alert>
        </Snackbar>
      </SubscriptionAction>
    </React.Fragment>
  );
};

export default ReconcileSubscription;
